import { useState } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import Iconify from '@emfood/shared/src/components/iconify';
import GoBackHeader from '@emfood/shared/src/components/go-back-header/go-back-header';
import {
  createSortSchema,
  ACHelmetContainer,
  createPaginationSchema,
  useSyncTableSearchParams,
  getTableModelsFromSearchParams,
} from '@emfood/shared';

import { GridSortModel, GridPaginationModel } from '@mui/x-data-grid';
import { Card, Stack, Button, Typography, CardContent } from '@mui/material';

import { useGetGenerationHistory } from 'src/api/hooks/api-hooks';
import { GenerationHistoryItemPreviewProps } from 'src/api/models/generation-history';

import { useSettingsContext } from 'src/components/settings';

import PreviewStoryDialog from './preview-story-dialog';
import GenerateNewStoryDialog from './new-story-dialog';
import GenerationHistoryTable from './generation-history-table';

export const StoriesSearchParamsSchema = createPaginationSchema({
  defaultPageSize: 10,
  allowedPageSizes: [5, 10, 25],
}).concat(
  createSortSchema(['createdAt'] as const, {
    defaultSortBy: 'createdAt',
    defaultSortOrder: 'desc',
  })
);

const routeApi = getRouteApi('/stories/');

export default function GenerationHistoryView() {
  const settings = useSettingsContext();
  const navigate = routeApi.useNavigate();
  const routeSearch = routeApi.useSearch();

  const { paginationModel: searchPaginationModel, sortModel: searchSortModel } =
    getTableModelsFromSearchParams(routeSearch);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    searchPaginationModel ?? { page: 0, pageSize: 10 }
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(
    searchSortModel ?? [{ field: 'createdAt', sort: 'desc' }]
  );

  useSyncTableSearchParams({ navigate, paginationModel, sortModel });

  const [openDialog, setOpenDialog] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [selectedStory, setSelectedStory] = useState<GenerationHistoryItemPreviewProps | null>(
    null
  );

  const { data: generationHistoryData, isLoading } = useGetGenerationHistory(
    paginationModel,
    sortModel[0]
  );

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handlePreviewStory = (story: GenerationHistoryItemPreviewProps) => {
    setSelectedStory(story);
    setOpenPreviewDialog(true);
  };

  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
    setSelectedStory(null);
  };

  return (
    <ACHelmetContainer title="Generation History" maxWidth={settings.themeStretch ? false : 'xl'}>
      <GoBackHeader
        heading="Stories"
        disableGoBack
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleOpenDialog}
          >
            Generate New Story
          </Button>
        }
      />
      <Stack spacing={3}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">Generation History</Typography>

              <GenerationHistoryTable
                generationHistory={generationHistoryData?.data}
                generationHistoryTotalCount={generationHistoryData?.totalRecords}
                isLoading={isLoading}
                paginationModel={paginationModel}
                sortModel={sortModel}
                onSortChanged={handleSortModelChange}
                onPaginationModelChanged={handlePaginationModelChange}
                onPreviewStory={handlePreviewStory}
              />
            </Stack>
          </CardContent>
        </Card>

        <GenerateNewStoryDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onOpenPreview={handlePreviewStory}
        />
        <PreviewStoryDialog
          open={openPreviewDialog}
          onClose={handleClosePreviewDialog}
          storyData={selectedStory}
        />
      </Stack>
    </ACHelmetContainer>
  );
}
